<script lang="ts">
    import { createEventDispatcher } from "svelte";

    export let max: number;
    export let value: number;
    export let loaded = 0;
    export let withProgress = false;
    const dispatcher = createEventDispatcher();
    let input: null | HTMLInputElement = null;
    const handleChange = () => {
        if (!input) return;
        dispatcher("change", parseFloat(input.value));
    };
</script>

<div class="gw-video-player-slider">
    {#if withProgress}
        <progress
            class="gw-video-player-slider__progress"
            {max}
            value={loaded}
            aria-label="Buffered"
            aria-valuemin={0}
            aria-valuemax={max}
            aria-valuenow={loaded}
            aria-valuetext="1%">% buffered</progress
        >
    {:else}
        <div class="gw-video-player-slider__backdrop" />
    {/if}
    <input
        bind:this={input}
        {max}
        {value}
        on:change={handleChange}
        style={`--gw-vp-value: ${(value / max) * 100}%`}
        class="gw-video-player-slider__input"
        type="range"
        step="0.01"
        min="0"
        autocomplete="off"
        aria-label="Scrubber"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext="00:23 of 03:51"
        aria-orientation="horizontal"
    />
</div>

<style>
    .gw-video-player-slider {
        position: relative;
        width: 100%;
        height: 20px;
    }
    .gw-video-player-slider__input {
        display: block;
        position: absolute;
        top: 50%;
        left: 0;
        box-sizing: border-box;
        width: 100%;
        height: var(--gw-vp-slider-track-height);
        margin: 0;
        transform: translateY(-50%);
        transition: box-shadow 0.3s ease;
        border: 0;
        border-radius: 0;
        outline: 0;
        background: transparent;
        color: var(--gw-vp-slider-track-color);
        cursor: pointer;
        user-select: none;
        touch-action: manipulation;
        appearance: none;
    }

    .gw-video-player-slider__backdrop {
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: var(--gw-vp-slider-track-height);
        transform: translateY(-50%);
        transition: box-shadow 0.3s ease;
        border: 0;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: transparent;
        background-color: var(--gw-vp-slider-progress-color);
        user-select: none;
    }

    /* SLIDER ELEMENT FOR CHROME */

    .gw-video-player-slider__input::-webkit-slider-runnable-track {
        height: var(--gw-vp-slider-track-height);
        transition: box-shadow 0.3s ease;
        border: 0;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: transparent;
        background-color: var(--gw-vp-slider-track-bg);
        background-image: linear-gradient(
            to right,
            currentColor var(--gw-vp-value, 0%),
            transparent var(--gw-vp-value, 0%)
        );
        user-select: none;
    }

    .gw-video-player-slider__input::-webkit-slider-thumb {
        position: relative;
        width: var(--gw-vp-slider-thumb-diameter);
        height: var(--gw-vp-slider-thumb-diameter);
        transform: translateY(-25%);
        transition: all 0.2s ease;
        border: 0;
        border-radius: 100%;
        opacity: 0;
        background: var(--gw-vp-slider-thumb-color);
        appearance: none;
    }

    .gw-video-player-slider__input:hover::-webkit-slider-thumb {
        opacity: 1;
    }

    /* SLIDER ELEMENT FOR FIREFOX */

    .gw-video-player-slider__input::-moz-range-track {
        height: var(--gw-vp-slider-track-height);
        transition: box-shadow 0.3s ease;
        border: 0;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: transparent;
        background-color: var(--gw-vp-slider-track-bg);
        background-image: linear-gradient(
            to right,
            currentColor var(--gw-vp-value, 0%),
            transparent var(--gw-vp-value, 0%)
        );
        user-select: none;
    }

    .gw-video-player-slider__input::-moz-range-thumb {
        position: relative;
        width: var(--gw-vp-slider-thumb-diameter);
        height: var(--gw-vp-slider-thumb-diameter);
        transition: all 0.2s ease;
        border: 0;
        border-radius: 100%;
        opacity: 0;
        background: var(--gw-vp-slider-thumb-color);
    }
    .gw-video-player-slider__input:hover::-moz-range-thumb {
        opacity: 1;
    }

    .gw-video-player-slider__input::-moz-range-progress {
        height: var(--gw-vp-slider-track-height);
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: currentColor;
    }

    /* PROGRESS ELEMENT */

    .gw-video-player-slider__progress {
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: var(--gw-vp-slider-track-height);
        transform: translateY(-50%);
        border: none;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: var(--gw-vp-slider-progress-color);
        color: var(--gw-vp-slider-buffered-color);
        appearance: none;
    }

    /* PROGRESS ELEMENT FOR CHROME */

    .gw-video-player-slider__progress::-webkit-progress-value {
        min-width: var(--gw-vp-slider-track-height);
        transition: width 0.2s ease 0s;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: currentcolor;
    }

    .gw-video-player-slider__progress::-webkit-progress-bar {
        background-color: transparent;
    }

    .gw-video-player-slider__progress::-webkit-progress-value {
        min-width: var(--gw-vp-slider-track-height);
        transition: width 0.2s ease 0s;
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: currentcolor;
    }
    /* PROGRESS ELEMENT FOR FIREFOX */
    .gw-video-player-slider__progress::-moz-progress-bar {
        border-radius: calc(var(--gw-vp-slider-track-height) / 2);
        background: currentcolor;
    }
</style>
